// What the confirmation windows say about a row that closes off its plan (OH-205 spec §4): the «Plan X → Y (±Δ, ±n%)»
// line with what Confirm does about it, and the size of the deviation the armed Confirm names. Text only, built from
// the comparisons and outcomes of lib/planDeviation — each window decides when to show it, and every window that does
// reads alike (the queue window, the booking row's window and the batch share this one place).
import { formatMinor } from '@/lib/money';
import { deviationBp, type PlanComparison, type SettleOutcome } from '@/lib/planDeviation';

/** Basis points as a percentage with two decimals, in integers: 427 → «4.27%»; the sign is the caller's. */
export function percentOf(bp: number): string {
  return `${Math.floor(Math.abs(bp) / 100)}.${String(Math.abs(bp) % 100).padStart(2, '0')}%`;
}

/**
 * U+2060 WORD JOINER, invisible: it forbids a line break on either side. UAX #14 lets a line break after a hyphen when a
 * letter or a currency symbol follows, and in the batch's narrow row column that left «(-» at the end of one line and
 * «RSD 4.50, …» at the start of the next — a minus on its own reads as a positive amount.
 */
const WORD_JOINER = '\u2060';

/**
 * An amount with its currency, a plus in front of a positive one (formatMinor already puts the minus in front of a
 * negative). The sign is glued to its amount with a word joiner, so a line never breaks between them.
 */
export function signedMinor(minor: number, currency: string): string {
  const amount = formatMinor(minor, currency);
  if (minor > 0) return `+${WORD_JOINER}${amount}`;
  return amount.replace(/^[-\u2212]/, (sign) => `${sign}${WORD_JOINER}`);
}

/**
 * «Plan X → Y (±Δ, ±n%)» (spec §4): the plan, what was paid against it and the difference, all in `comparedIn` — the
 * comparison's own currency. A plan that is not positive has no percentage.
 */
export function planLine(c: PlanComparison, comparedIn: string): string {
  const bp = deviationBp(c.paidMinor, c.plannedMinor);
  const sign = bp < 0 ? '-' : bp > 0 ? '+' : '';
  const shown = [signedMinor(c.paidMinor - c.plannedMinor, comparedIn), ...(c.plannedMinor > 0 ? [`${sign}${percentOf(bp)}`] : [])];
  return `Plan ${formatMinor(c.plannedMinor, comparedIn)} → ${formatMinor(c.paidMinor, comparedIn)} (${shown.join(', ')})`;
}

/**
 * The row's whole new plan, in the row's currency: the amount the plan becomes and — only when part of the row was
 * confirmed earlier — how much of it, so the number is not read as this payment alone (IR-01). «the plan becomes Z» and
 * the queue window's «restated to Z» are both written by it, so the two name the plan the same way.
 */
export function newPlanText(newPlanMinor: number, settledSoFarMinor: number, currency: string): string {
  const plan = formatMinor(newPlanMinor, currency);
  return settledSoFarMinor > 0 ? `${plan}, including ${formatMinor(settledSoFarMinor, currency)} confirmed earlier` : plan;
}

/**
 * «Plan X → Y (±Δ, ±n%): what Confirm does about it» (spec §4), in the comparison's own currency; the outcome's
 * amounts in the row's currency (the plan, the rest) or the account's (a fee, an exchange difference). A plan that is
 * not positive has no percentage; null when the row closes at plan and there is nothing to show.
 */
export function outcomeText(
  c: PlanComparison,
  o: SettleOutcome,
  comparedIn: string,
  rowCurrency: string,
  accountCurrency: string,
): string | null {
  let phrase: string;
  switch (o.kind) {
    case 'none':
      return null;
    case 'restate':
      phrase = `the plan becomes ${newPlanText(o.newPlanMinor, o.settledSoFarMinor, rowCurrency)}`;
      break;
    case 'partial':
      phrase = `${formatMinor(o.restMinor, rowCurrency)} stays in the queue`;
      break;
    case 'fee':
      phrase = `the bank kept ${formatMinor(o.feeMinor, accountCurrency)} — booked as a fee`;
      break;
    case 'fx':
      phrase = `${formatMinor(Math.abs(o.deltaMinor), accountCurrency)} to exchange difference — the row closes at plan`;
      break;
  }
  return `${planLine(c, comparedIn)}: ${phrase}`;
}

/**
 * What the armed Confirm confirms, after «Confirm — »: the biggest deviation of the comparisons that ask for the
 * second press; no percentage for a plan that is not positive.
 */
export function offPlanText(pressing: readonly PlanComparison[]): string {
  // Defensive: a plan that is not positive comes with the non-advisory blocker `amount_rounds_to_zero`, so Confirm is
  // closed and never arms — yet the outcome line can still show it, because the comparison runs while the draft is clean.
  if (pressing.some((c) => c.plannedMinor <= 0)) return 'off plan';
  const worst = Math.max(...pressing.map((c) => Math.abs(deviationBp(c.paidMinor, c.plannedMinor))));
  return `${percentOf(worst)} off plan`;
}
